import React from 'react';
import clsx from 'clsx';
import { Lock, RotateCcw, Save } from 'lucide-react';
import { Button } from '@/components/Button';

/**
 * Shared layout for every Settings page: one header, then sections laid out
 * as "explanation on the left, controls on the right".
 *
 * Breakpoints are container queries on the page, not the viewport: next to
 * the app sidebar and the settings nav, a 1024 px screen leaves ~460 px, and
 * viewport `md`/`lg` columns squeezed the controls to ~140 px.
 */
export function SettingsPage({ eyebrow, title, description, actions, children, className }: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={clsx('@container min-w-0', className)}>
      <header className="flex flex-col gap-4 border-b border-border pb-6 @xl:flex-row @xl:items-end @xl:justify-between">
        <div className="min-w-0">
          {eyebrow && <div className="mb-1 text-2xs font-semibold uppercase tracking-[0.16em] text-primary">{eyebrow}</div>}
          <h1 className="font-display text-2xl font-bold tracking-tight text-text-primary">{title}</h1>
          {description && <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-text-muted">{description}</p>}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </header>
      <div className="divide-y divide-border">{children}</div>
    </div>
  );
}

export function SettingsSection({ title, description, aside, children, id }: {
  title: string;
  description?: React.ReactNode;
  /** Extra left-column content under the description (e.g. a lock note or a link). */
  aside?: React.ReactNode;
  children: React.ReactNode;
  id?: string;
}) {
  return (
    <section id={id} className="grid gap-5 py-7 @2xl:grid-cols-[minmax(0,220px)_minmax(0,1fr)] @2xl:gap-8 @4xl:grid-cols-[minmax(0,280px)_minmax(0,1fr)] @4xl:gap-10">
      <div className="min-w-0 @2xl:sticky @2xl:top-24 @2xl:self-start">
        <h2 className="text-sm font-semibold text-text-primary">{title}</h2>
        {description && <div className="mt-1 text-xs leading-relaxed text-text-muted">{description}</div>}
        {aside && <div className="mt-3">{aside}</div>}
      </div>
      <div className="min-w-0 space-y-4">{children}</div>
    </section>
  );
}

/** White panel for a section's controls; `footer` is the action bar (Save / Discard). */
export function SettingsCard({ children, footer, padded = true, className }: {
  children: React.ReactNode;
  footer?: React.ReactNode;
  padded?: boolean;
  className?: string;
}) {
  return (
    <div className={clsx('overflow-hidden rounded-2xl border border-border bg-surface shadow-soft', className)}>
      <div className={clsx(padded && 'p-5')}>{children}</div>
      {footer && <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border bg-surface-alt/50 px-5 py-3">{footer}</div>}
    </div>
  );
}

/** Read-only key / value line (country, currency, plan…). */
export function SettingsRow({ icon, label, hint, value, action, locked }: {
  icon?: React.ReactNode;
  label: string;
  hint?: string;
  value: React.ReactNode;
  action?: React.ReactNode;
  locked?: boolean;
}) {
  return (
    <div className="flex items-center gap-3 px-5 py-3.5">
      {icon && <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface-alt text-text-secondary">{icon}</span>}
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-text-primary">{label}</div>
        {hint && <div className="text-2xs text-text-muted">{hint}</div>}
      </div>
      <div className="flex shrink-0 items-center gap-2 text-sm font-semibold text-text-primary">
        {value}
        {locked && <Lock size={12} className="text-text-muted" aria-label="Managed by the platform" />}
      </div>
      {action}
    </div>
  );
}

export function Switch({ checked, onChange, disabled, label }: { checked: boolean; onChange: (next: boolean) => void; disabled?: boolean; label?: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={clsx(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-soft disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'border-primary bg-gold-gradient' : 'border-border-strong bg-surface-alt',
      )}
    >
      <span className={clsx('inline-block h-[18px] w-[18px] translate-x-[2px] rounded-full bg-white shadow-soft transition-transform', checked && 'translate-x-[22px]')} />
    </button>
  );
}

/** A labelled switch with its explanation — the standard "feature on/off" row. */
export function SettingsToggle({ label, description, checked, onChange, disabled, badge, icon }: {
  label: string;
  description?: React.ReactNode;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  badge?: React.ReactNode;
  icon?: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-4 px-5 py-4">
      {icon && <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">{icon}</span>}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-text-primary">{label}{badge}</div>
        {description && <div className="mt-1 text-xs leading-relaxed text-text-muted">{description}</div>}
      </div>
      <Switch checked={checked} onChange={onChange} disabled={disabled} label={label} />
    </div>
  );
}

/** Small number tile for a page's headline counts. */
export function SettingsStat({ label, value, hint, tone = 'default' }: { label: string; value: React.ReactNode; hint?: string; tone?: 'default' | 'gold' | 'success' | 'warning' | 'danger' }) {
  return (
    <div className="rounded-2xl border border-border bg-surface px-4 py-3.5 shadow-soft">
      <div className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">{label}</div>
      <div className={clsx('mt-1 font-display text-2xl font-bold tabular-nums tracking-tight',
        tone === 'gold' ? 'text-primary' : tone === 'success' ? 'text-success' : tone === 'warning' ? 'text-warning' : tone === 'danger' ? 'text-danger' : 'text-text-primary')}>
        {value}
      </div>
      {hint && <div className="mt-0.5 text-2xs text-text-muted">{hint}</div>}
    </div>
  );
}

/**
 * Floating "unsaved changes" bar for long pages, where a card footer would sit below the fold.
 * Render it as the page's last child while there is something to save.
 */
export function SettingsSaveBar({ message = 'Unsaved changes', note, onDiscard, onSave, saving, saveLabel = 'Save changes', disabled }: {
  message?: string;
  note?: React.ReactNode;
  onDiscard?: () => void;
  onSave: () => void;
  saving?: boolean;
  saveLabel?: string;
  disabled?: boolean;
}) {
  return (
    <div className="sticky bottom-4 z-10 mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-surface/95 px-5 py-3 shadow-elevated backdrop-blur">
      <span className="inline-flex flex-wrap items-center gap-2 text-sm font-medium text-text-primary">
        <span className="h-2 w-2 rounded-full bg-warning" />
        {message}
        {note}
      </span>
      <div className="flex gap-2">
        {onDiscard && <Button variant="ghost" size="sm" iconLeft={<RotateCcw size={14} />} onClick={onDiscard} disabled={saving}>Discard</Button>}
        <Button size="sm" iconLeft={<Save size={14} />} loading={saving} disabled={disabled} onClick={onSave}>{saveLabel}</Button>
      </div>
    </div>
  );
}
